import type { Section } from "../types";
import type { IntelResultSet } from "./streamIntelChat";

/**
 * Chart-reference validator/mapper — the engine half of "model proposes,
 * engine disposes" (map ticket 40 R6). The model (future wire contract)
 * attaches a chart REFERENCE proposal to a result set: a closed-enum `kind`
 * plus column encodings. This module validates the proposal against the
 * set's REAL shape and binds the data itself — the model never authors chart
 * data. An invalid proposal returns a reasoned rejection so the caller can
 * fall back to the VISIBLE table, never a blank.
 *
 * v1 CANDIDATE enum — final membership is pinned at prototype review.
 * Census basis (prod answer shapes): single-stat 28%, 1×N KPI groups ~17%,
 * bar ~20% (donut subset 7%), table-only ~30%, line ≈ 0% (the prod schema is
 * a point-in-time snapshot — no time axis).
 */
export type ChartRefKind = "bar" | "donut" | "bar_list" | "kpi_row";

export type ChartRef = {
  kind: ChartRefKind;
  /** Label/axis column name. Omitted → first non-numeric column. */
  x?: string;
  /** Value column names. Omitted → all numeric columns (minus x). */
  y?: string[];
  /** Proposed title — first link of the title chain. */
  label?: string;
};

export type ChartRefVerdict =
  | { ok: true; section: Section }
  | { ok: false; reason: string };

const CHART_REF_KINDS: readonly ChartRefKind[] = ["bar", "donut", "bar_list", "kpi_row"];

type Cell = string | number | boolean | null;

/** A cell is numeric-ish when it is a finite number or a string that parses
 *  to one ("42", "11.4"). Booleans and free text are not. */
function isNumericCell(cell: Cell): boolean {
  if (typeof cell === "number") return Number.isFinite(cell);
  if (typeof cell === "string") {
    const trimmed = cell.trim();
    return trimmed !== "" && Number.isFinite(Number(trimmed));
  }
  return false;
}

/** Column is numeric iff every non-null cell is numeric-ish AND at least one
 *  non-null cell exists (an all-null column has nothing to plot). Exported for
 *  ResultSetCard's display-column reorder (name-ish columns lead). */
export function isNumericColumn(set: IntelResultSet, colIndex: number): boolean {
  let nonNull = 0;
  for (const row of set.rows) {
    const cell = row[colIndex];
    if (cell == null) continue;
    nonNull += 1;
    if (!isNumericCell(cell)) return false;
  }
  return nonNull > 0;
}

function toNumber(cell: Cell): number {
  // Null y-cells bind as 0 — column-level numeric detection already ran, so
  // this only smooths sparse cells, it never coerces text.
  if (cell == null) return 0;
  return typeof cell === "number" ? cell : Number(String(cell).trim());
}

function humanize(column: string): string {
  return column.replace(/_/g, " ");
}

/** Title chain (mirrors ResultSetCard's label → column-derived chain):
 *  ref.label → set.label → humanized column names. */
function chartTitle(set: IntelResultSet, ref: ChartRef): string {
  const proposed = ref.label?.trim();
  if (proposed) return proposed;
  const setLabel = set.label?.trim();
  if (setLabel) return setLabel;
  const names = set.columns
    .map((c) => humanize(c.trim()))
    .filter(Boolean)
    .map((c) => c.charAt(0).toUpperCase() + c.slice(1));
  const head = names.slice(0, 3).join(" · ");
  return names.length > 3 ? `${head} · …` : head;
}

/** Resolved column encodings, or a rejection. x is null for kinds that don't
 *  use a label column (kpi_row). */
function resolveEncodings(
  set: IntelResultSet,
  ref: ChartRef,
  needsX: boolean,
): { x: string | null; y: string[] } | { reason: string } {
  // Every referenced column must exist on the real set — reject with the
  // offending name so the proposal bug is visible.
  for (const name of [...(ref.x ? [ref.x] : []), ...(ref.y ?? [])]) {
    if (!set.columns.includes(name)) {
      return { reason: `column "${name}" is not in the result set` };
    }
  }

  const numericCols = set.columns.filter((_c, i) => isNumericColumn(set, i));

  let x: string | null = null;
  if (needsX) {
    x = ref.x ?? set.columns.find((c) => !numericCols.includes(c)) ?? null;
    if (x == null) {
      return { reason: "no label column to use as x — every column is numeric" };
    }
  }

  let y: string[];
  if (ref.y != null) {
    if (ref.y.length === 0) return { reason: "y must name at least one column" };
    const nonNumeric = ref.y.find((c) => !numericCols.includes(c));
    if (nonNumeric != null) {
      return { reason: `column "${nonNumeric}" is not numeric` };
    }
    y = ref.y;
  } else {
    y = numericCols.filter((c) => c !== x);
    if (y.length === 0) return { reason: "no numeric columns to plot" };
  }

  return { x, y };
}

/**
 * Validate a chart-ref proposal against a result set and bind the data into a
 * renderer Section (../types — the vocabulary DashboardRenderer dispatches on).
 *
 * IMPORTANT: `kind` is a closed enum checked at runtime. The legacy
 * SectionChart silently renders any unknown chart_type as an AREA chart
 * (sections/SectionChart.tsx final else-branch) — an unvalidated kind must
 * never pass through to it.
 */
export function resolveChartRef(set: IntelResultSet, ref: ChartRef): ChartRefVerdict {
  if (!CHART_REF_KINDS.includes(ref.kind)) {
    return { ok: false, reason: `unknown chart kind "${String(ref.kind)}"` };
  }

  const rowCount = set.rows.length;

  if (ref.kind === "kpi_row") {
    if (rowCount === 1 && set.columns.length === 1) {
      // The 1×1 shape already renders as a KPI tile deterministically
      // (IntelChat's R5 tile floor) — a proposal adds nothing.
      return { ok: false, reason: "handled by tile floor" };
    }
    if (rowCount !== 1) {
      return { ok: false, reason: `kpi_row needs exactly 1 row (got ${rowCount})` };
    }
    const enc = resolveEncodings(set, ref, false);
    if ("reason" in enc) return { ok: false, reason: enc.reason };
    // 2–8 mirrors the ratified 1×(2–8) KPI-strip floor (46 reconcile: one
    // element, one threshold — the server proposal gate matches).
    if (enc.y.length < 2 || enc.y.length > 8) {
      return {
        ok: false,
        reason: `kpi_row needs 2–8 numeric columns (got ${enc.y.length})`,
      };
    }
    const row = set.rows[0];
    return {
      ok: true,
      section: {
        kind: "kpi_row",
        items: enc.y.map((col) => ({
          label: humanize(col),
          value: String(row[set.columns.indexOf(col)] ?? ""),
        })),
      },
    };
  }

  const enc = resolveEncodings(set, ref, true);
  if ("reason" in enc) return { ok: false, reason: enc.reason };
  const xIndex = set.columns.indexOf(enc.x as string);
  const title = chartTitle(set, ref);

  if (ref.kind === "bar") {
    if (rowCount < 2 || rowCount > 30) {
      return { ok: false, reason: `bar needs 2–30 rows (got ${rowCount})` };
    }
    return {
      ok: true,
      section: {
        kind: "chart",
        chart_type: "bar",
        title,
        x_key: enc.x as string,
        data: set.rows.map((row) => {
          const datum: Record<string, string | number> = {
            [enc.x as string]: String(row[xIndex] ?? ""),
          };
          for (const col of enc.y) datum[col] = toNumber(row[set.columns.indexOf(col)]);
          return datum;
        }),
        // Multi-y renders as grouped bars (one Bar per series in SectionChart).
        series: enc.y.map((col) => ({ data_key: col, name: humanize(col) })),
      },
    };
  }

  if (ref.kind === "donut") {
    // Old skill rule capped pies at ≤6 slices and the renderer palette cycles
    // at 5 (SectionChart SERIES_COLORS) — enforce ≤8 hard, ≤6 preferred.
    if (rowCount < 2 || rowCount > 8) {
      return { ok: false, reason: `donut needs 2–8 rows (got ${rowCount})` };
    }
    if (enc.y.length !== 1) {
      return {
        ok: false,
        reason: `donut needs exactly 1 numeric value column (got ${enc.y.length})`,
      };
    }
    const yIndex = set.columns.indexOf(enc.y[0]);
    if (set.rows.some((row) => toNumber(row[yIndex]) < 0)) {
      return { ok: false, reason: "donut values must all be ≥ 0" };
    }
    return {
      ok: true,
      section: {
        kind: "chart",
        chart_type: "pie", // renders as a donut (innerRadius) in SectionChart
        title,
        x_key: enc.x as string,
        data: set.rows.map((row) => ({
          [enc.x as string]: String(row[xIndex] ?? ""),
          [enc.y[0]]: toNumber(row[yIndex]),
        })),
        series: [{ data_key: enc.y[0], name: humanize(enc.y[0]) }],
      },
    };
  }

  // bar_list
  if (rowCount < 1 || rowCount > 30) {
    return { ok: false, reason: `bar_list needs 1–30 rows (got ${rowCount})` };
  }
  if (enc.y.length !== 1) {
    return {
      ok: false,
      reason: `bar_list needs exactly 1 numeric value column (got ${enc.y.length})`,
    };
  }
  const yIndex = set.columns.indexOf(enc.y[0]);
  return {
    ok: true,
    section: {
      kind: "bar_list",
      title,
      // Descending sort is a presentation heuristic — allowed (evidence
      // selection is not; nothing is dropped).
      items: set.rows
        .map((row) => ({
          label: String(row[xIndex] ?? ""),
          value: toNumber(row[yIndex]),
        }))
        .sort((a, b) => b.value - a.value),
    },
  };
}
